import { useContext, useEffect, useRef, useState } from "react";
import PagedList, {
  IListPageColumn,
  IPagedListRefType,
} from "../../common/PagedList";
import { getRents, getMetadata, getRenter } from "../../services/assetServices";
import { useAtomValue, useSetAtom } from "jotai";
import { useLocation, useNavigate } from "react-router-dom";
import { profileDataAtom } from "../../atoms/authAtoms";
import {
  clearMessageAtom,
  errorMessageAtom,
  isInProgressAtom,
} from "../../atoms/messageBarAtoms";
import { appRoles } from "../../authConfig";
import AppContext from "../AppContext";

const Rents = () => {
  const query = new URLSearchParams(useLocation().search);
  const renterId = Number.parseInt(query.get("id") ?? "");

  const profileData = useAtomValue(profileDataAtom);
  const navigate = useNavigate();

  const [selectedRentId, setSelectedRentId] = useState<number>();

  const pagedListRef = useRef<IPagedListRefType>(null);

  const context = useContext(AppContext);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const setIsInProgress = useSetAtom(isInProgressAtom);
  const clearMessage = useSetAtom(clearMessageAtom);
  const columns = useRef<IListPageColumn[]>([]);

  const fetchData = async () => {
    const abortController = new AbortController();
    setIsInProgress(true);

    try {
      const abortController = new AbortController();
      const renter = await getRenter(abortController, renterId);
      const metadata = await getMetadata(abortController, "Rent");
      const otherInfoFields = metadata?.fields.filter((mf) => mf.showInGrid);
      otherInfoFields?.forEach((mf) => {
        if (!columns.current.find((c) => c.key === mf.name)) {
          columns.current.push({
            key: mf.name,
            name: mf.name,
            minWidth: 100,
            onRender:
              mf.fieldType !== "Image"
                ? undefined
                : (item: any) => (
                    <img src={item[mf.name]} style={{ width: 20 }} alt="" /> //:
                  ),
          });
        }
      });
      clearMessage();
      context.setSelectedTab(`Rents of ${renter.name}`);
    } catch (error: any) {
      setErrorMessage(error.message);
    } finally {
      setIsInProgress(false);
    }
    return () => {
      abortController.abort();
    };
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <>
      <PagedList
        selectedTab="Rents"
        ref={pagedListRef}
        listHeigh={525}
        columns={[
          {
            key: "id",
            name: "Id",
            minWidth: 25,
            maxWidth: 50,
            isResizable: true,
          },
          {
            key: "code",
            name: "Code",
            minWidth: 70,
            maxWidth: 100,
            isResizable: true,
          },
          {
            key: "assetName",
            name: "Asset",
            minWidth: 25,
            maxWidth: 50,
            isResizable: true,
          },
          {
            key: "createdAt",
            name: "Created at",
            minWidth: 125,
            maxWidth: 155,
            isResizable: true,
          },
          {
            key: "fromDate",
            name: "From",
            minWidth: 125,
            maxWidth: 155,
            isResizable: true,
          },
          {
            key: "toDate",
            name: "To",
            minWidth: 125,
            maxWidth: 155,
            isResizable: true,
          },
          ...columns.current,
        ]}
        getAction={async (abortController, pageSize, pageNo, orderBy, search) => {
          const data = await getRents(
            abortController,
            pageSize,
            pageNo,
            renterId,
            orderBy,
            search,
          );

          data.items = data.items.map((rent) => {
            const newRent: any = {
              ...rent,
              createdAt: new Date(rent.createdAt + "Z").toLocaleString(),
              fromDate: new Date(rent.fromDate + "Z").toLocaleString(),
              toDate: new Date(rent.toDate + "Z").toLocaleString(),
            };
            const otherInfoParts = rent.otherInfo.split("|");
            columns.current.map((mf, index) => {
              if (mf?.name) {
                const part = otherInfoParts[index];
                newRent[mf.name] = part;
              }
            });
            return newRent;
          });
          return data;
        }}
        commandBarItems={[
          {
            key: "back",
            text: "",
            iconProps: { iconName: "Back" },
            onClick: () => navigate("/renters"),
          },
          {
            key: "newRent",
            text: "New",
            iconProps: { iconName: "Add" },
            onClick: () => navigate(`/newRent?renterId=${renterId}`),
            disabled: !profileData.roles.includes(appRoles.Admin),
          },
          {
            key: "editRent",
            text: "Edit",
            iconProps: { iconName: "Edit" },
            onClick: () =>
              navigate(`/editRent?renterId=${renterId}&id=${selectedRentId}`),
            disabled:
              !profileData.roles.includes(appRoles.Admin) || !selectedRentId,
          },
        ]}
        onSelectionChange={(selection: any) => {
          setSelectedRentId(Number(selection.id));
        }}
        onRowDoubleClicked={(selection: any) => {
          navigate(`/editRent?renterId=${renterId}&id=${selection.id}`);
        }}
      ></PagedList>
    </>
  );
};

export default Rents;
